/**
 * HierarchicalLocationSelector
 * Searchable combobox for selecting locations, grouped by country.
 * Supports single-select and multi-select modes.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, X, MapPin, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Spinner } from '@/components/ui';
import { getFullHierarchicalLocations, filterHierarchy, filterLocationsByTenancy } from '../../services/hierarchicalLocationService';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { useDeviceVisibility, filterDevicesByTier } from '../../hooks/useDeviceVisibility';
import { useAuth } from '../../contexts/AuthContext';
import type { Region, Country, Location } from '@simcheck/shared-types';

// Synthetic group id for the org's own locations — always rendered first (#202).
const MY_LOCATIONS_GROUP_ID = '__my_locations__';

// --- Props ---

interface BaseProps {
  disabled?: boolean;
  placeholder?: string;
  /**
   * Offer only the caller's OWN sites — the org's locations plus any location hosting one of
   * its private devices (#283). Used by surfaces that can only ever act on private edges, where
   * offering the shared SimCheck network means offering a choice that is guaranteed to fail.
   *
   * Deliberately reuses the `myLocationIds` set this component already computes for the
   * "My locations" group rather than letting each caller re-derive ownership — the predicate is
   * subtler than `location.ownerOrgId === orgId` (re-homing a device does not move its
   * location), and a second copy of it would drift.
   */
  onlyMine?: boolean;
}

interface SingleSelectProps extends BaseProps {
  mode?: 'single';
  selectedLocationId: string | null;
  onSelectLocation: (locationId: string | null) => void;
}

interface MultiSelectProps extends BaseProps {
  mode: 'multi';
  selectedLocationIds: string[];
  onSelectLocations: (locationIds: string[]) => void;
}

export type HierarchicalLocationSelectorProps = SingleSelectProps | MultiSelectProps;

// --- Internal types ---

interface LocationOption {
  id: string;
  locationName: string;
  countryName: string;
  countryFlag: string;
  countryId: string;
  regionName: string;
  modemCount: number;
  availableModemCount: number;
}

interface CountryGroup {
  countryId: string;
  heading: string;
  locations: LocationOption[];
}

// --- Component ---

export const HierarchicalLocationSelector: React.FC<HierarchicalLocationSelectorProps> = (props) => {
  const {
    disabled = false,
    placeholder,
    onlyMine = false,
  } = props;

  const isMulti = props.mode === 'multi';
  const visibilityFlags = useDeviceVisibility();
  const { activeOrganizationId } = useAuth();

  const [open, setOpen] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [myLocationIds, setMyLocationIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHierarchy();
  }, [activeOrganizationId]);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);

      const [locationData, devices] = await Promise.all([
        getFullHierarchicalLocations(),
        // Force-refresh: a stale cached snapshot (before the Pi heartbeat populated
        // `modems`) leaves the dropdown stuck at 0/0. Matches the admin page.
        edgeDeviceService.getDevices(true).catch((err) => {
          console.error('Failed to load edge devices for location selector:', err);
          return [];
        }),
      ]);

      // Filter devices by tier based on org access
      const visibleDevices = filterDevicesByTier(devices, visibilityFlags);

      // Tenancy (#202): "my" locations = locations my org owns, plus any
      // location hosting one of my org's private devices (covers a private
      // device parked at a shared location). Rendered as the first group.
      const mine = new Set<string>();
      if (activeOrganizationId) {
        for (const device of visibleDevices as any[]) {
          if (device.ownerOrgId === activeOrganizationId && device.locationId) {
            mine.add(device.locationId);
          }
        }
      }

      // Calculate modem counts per location from visible edge devices
      const locationModemCounts = new Map<string, { total: number; available: number }>();
      for (const device of visibleDevices) {
        if (device.locationId) {
          const existing = locationModemCounts.get(device.locationId) || { total: 0, available: 0 };
          const modemCount = device.modems?.length || 0;
          const deviceIsOnline = device.status === 'online';
          const availableCount = deviceIsOnline
            ? (device.modems?.filter(m => m.status !== 'disconnected' && m.status !== 'error').length || 0)
            : 0;
          locationModemCounts.set(device.locationId, {
            total: existing.total + modemCount,
            available: existing.available + availableCount,
          });
        }
      }

      // Privacy (#202): other orgs' private locations are never shown to
      // tenants (the client reads the raw collection, so filter here), and
      // synthetic demo locations stay scoped to their owning org even for
      // super admins. Use the shared helper — this used to be an inline copy
      // of the rule, which is exactly how it drifted out of sync.
      const enrichedLocations = filterLocationsByTenancy(
        locationData.locations,
        activeOrganizationId,
        visibilityFlags.isSuperAdmin,
        { hasDeviceAt: (id) => locationModemCounts.has(id) },
      )
        .map(location => {
          if (activeOrganizationId && location.ownerOrgId === activeOrganizationId) {
            mine.add(location.id);
          }
          return {
            ...location,
            modemCount: locationModemCounts.get(location.id)?.total ?? 0,
            availableModemCount: locationModemCounts.get(location.id)?.available ?? 0,
          };
        });

      // Only show locations that have visible devices — except the org's own
      // locations, which stay visible even while empty (a fresh default
      // location must be discoverable so the tenant understands where their
      // devices will land).
      const visibleLocations = visibilityFlags.isSuperAdmin
        ? enrichedLocations
        : enrichedLocations.filter(loc => locationModemCounts.has(loc.id) || mine.has(loc.id));

      // Filter hierarchy to remove empty countries/regions
      const filtered = filterHierarchy(locationData.regions, locationData.countries, visibleLocations);
      setRegions(filtered.regions);
      setCountries(filtered.countries);
      setLocations(visibleLocations);
      setMyLocationIds(mine);
    } catch (err) {
      console.error('Failed to load location hierarchy:', err);
      setError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  // Build grouped location options
  const { groups, optionsMap } = useMemo(() => {
    const regionMap = new Map(regions.map(r => [r.id, r]));
    const countryMap = new Map(countries.map(c => [c.id, c]));

    const options: LocationOption[] = locations.map(loc => {
      const country = countryMap.get(loc.countryId || '');
      const region = regionMap.get(loc.regionId || country?.regionId || '');
      return {
        id: loc.id,
        locationName: loc.displayName || loc.name,
        countryName: country?.name || '',
        countryFlag: country?.flag || '',
        countryId: loc.countryId || '',
        regionName: region?.name || '',
        modemCount: (loc as any).modemCount ?? 0,
        availableModemCount: (loc as any).availableModemCount ?? 0,
      };
    });

    // "My locations" first (#202): the org's own locations (and locations
    // hosting its private devices) form a dedicated top group; the rest are
    // grouped by country as before.
    const myOptions = options.filter(o => myLocationIds.has(o.id));
    // `onlyMine` drops the shared network entirely rather than merely ranking it second.
    const sharedOptions = onlyMine ? [] : options.filter(o => !myLocationIds.has(o.id));

    // Group by country, sorted alphabetically
    const countryGroups = new Map<string, CountryGroup>();
    for (const opt of sharedOptions) {
      if (!countryGroups.has(opt.countryId)) {
        countryGroups.set(opt.countryId, {
          countryId: opt.countryId,
          // Org default locations start with no country — label the bucket
          // instead of rendering an empty heading (super-admin view).
          heading: `${opt.countryFlag} ${opt.countryName}`.trim() || 'Org locations',
          locations: [],
        });
      }
      countryGroups.get(opt.countryId)!.locations.push(opt);
    }

    // Sort locations within each group
    for (const group of countryGroups.values()) {
      group.locations.sort((a, b) => a.locationName.localeCompare(b.locationName));
    }

    // Sort groups by country name
    const sortedGroups = Array.from(countryGroups.values()).sort((a, b) =>
      a.heading.localeCompare(b.heading)
    );

    if (myOptions.length > 0) {
      myOptions.sort((a, b) => a.locationName.localeCompare(b.locationName));
      sortedGroups.unshift({
        countryId: MY_LOCATIONS_GROUP_ID,
        heading: 'My locations',
        locations: myOptions,
      });
    }

    const map = new Map(options.map(o => [o.id, o]));
    return { groups: sortedGroups, optionsMap: map };
  }, [regions, countries, locations, myLocationIds, onlyMine]);

  // Selection helpers
  const isSelected = (id: string): boolean => {
    if (isMulti) {
      return (props as MultiSelectProps).selectedLocationIds.includes(id);
    }
    return (props as SingleSelectProps).selectedLocationId === id;
  };

  const handleSelect = (id: string) => {
    if (isMulti) {
      const multiProps = props as MultiSelectProps;
      const current = multiProps.selectedLocationIds;
      const next = current.includes(id)
        ? current.filter(x => x !== id)
        : [...current, id];
      multiProps.onSelectLocations(next);
    } else {
      const singleProps = props as SingleSelectProps;
      const next = singleProps.selectedLocationId === id ? null : id;
      singleProps.onSelectLocation(next);
      setOpen(false);
    }
  };

  const handleRemove = (id: string) => {
    if (isMulti) {
      const multiProps = props as MultiSelectProps;
      multiProps.onSelectLocations(multiProps.selectedLocationIds.filter(x => x !== id));
    }
  };

  // Toggle selection of all locations within a country group. If every
  // location in the group is already selected we deselect them; otherwise
  // we add the missing ones. Only meaningful in multi-select mode.
  const handleToggleCountry = (group: CountryGroup) => {
    if (!isMulti) return;
    const multiProps = props as MultiSelectProps;
    const ids = group.locations.map(l => l.id);
    const current = multiProps.selectedLocationIds;
    const allSelected = ids.every(id => current.includes(id));
    const next = allSelected
      ? current.filter(id => !ids.includes(id))
      : Array.from(new Set([...current, ...ids]));
    multiProps.onSelectLocations(next);
  };

  // Trigger label
  const getTriggerLabel = (): string => {
    if (isMulti) {
      const count = (props as MultiSelectProps).selectedLocationIds.length;
      if (count === 0) return placeholder || 'Select locations...';
      return `${count} location${count !== 1 ? 's' : ''} selected`;
    }
    const selectedId = (props as SingleSelectProps).selectedLocationId;
    if (!selectedId) return placeholder || 'Select location...';
    const opt = optionsMap.get(selectedId);
    if (!opt) return placeholder || 'Select location...';
    return `${opt.countryFlag} ${opt.locationName}`.trim();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" />
        <span>Loading locations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-status-error">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || groups.length === 0}
            className="w-full justify-between text-sm font-normal"
          >
            <span className="flex items-center gap-2 truncate">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {getTriggerLabel()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}>
            <CommandInput placeholder="Search locations..." />
            <CommandList>
              <CommandEmpty>
                {onlyMine
                  ? 'No sites of your own. This needs an edge device your organization owns.'
                  : 'No locations found.'}
              </CommandEmpty>
              {groups.map((group) => {
                const groupIds = group.locations.map(l => l.id);
                const allSelected =
                  isMulti && groupIds.every(id => isSelected(id));
                const showSelectAll = isMulti && group.locations.length > 1;
                const countryLabel = group.heading.replace(/^[^a-zA-Z]+/, '').trim() || 'country';
                return (
                  <CommandGroup key={group.countryId} heading={group.heading}>
                    {showSelectAll && (
                      <CommandItem
                        key={`${group.countryId}__all`}
                        // Make sure the row stays matchable when the user types
                        // "select all" or the country name in the search box.
                        value={`select all ${group.locations.length} in ${countryLabel}`}
                        onSelect={() => handleToggleCountry(group)}
                        className="text-primary font-medium"
                      >
                        <CheckCheck className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate">
                          {allSelected ? 'Deselect all in' : 'Select all in'} {countryLabel}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground shrink-0">
                          {group.locations.length} location{group.locations.length === 1 ? '' : 's'}
                        </span>
                      </CommandItem>
                    )}
                    {group.locations.map((loc) => (
                      <CommandItem
                        key={loc.id}
                        value={`${loc.locationName} ${loc.countryName} ${loc.regionName}`}
                        onSelect={() => handleSelect(loc.id)}
                        className={cn(
                          loc.availableModemCount === 0 && 'opacity-50'
                        )}
                      >
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            isSelected(loc.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="flex-1 truncate">
                          {group.countryId === MY_LOCATIONS_GROUP_ID && loc.countryFlag
                            ? `${loc.countryFlag} ${loc.locationName}`
                            : loc.locationName}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground shrink-0">
                          {loc.availableModemCount}/{loc.modemCount} modems
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Multi-select: show selected items as removable badges */}
      {isMulti && (props as MultiSelectProps).selectedLocationIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {(props as MultiSelectProps).selectedLocationIds.map((id) => {
            const opt = optionsMap.get(id);
            if (!opt) return null;
            return (
              <Badge key={id} variant="secondary" className="gap-1 pr-1">
                <MapPin className="h-3 w-3" />
                {opt.countryFlag} {opt.locationName}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  className="ml-0.5 rounded-sm hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};
